import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { ApiError, api } from '../api/index.js';
import type { User } from '../types/index.js';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Mark loading whenever auth state changes so ProtectedRoute
      // shows a spinner instead of bouncing to /login mid-fetch.
      setLoading(true);

      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get<User>('/api/users/me');
        setUser(response.data);
        setError(null);
      } catch (err) {
        setUser(null);
        setError(
          err instanceof ApiError ? err.message : 'Failed to load user profile. Please try again.',
        );
        // Sign Firebase back out so the app isn't stuck half-authenticated.
        // The next onAuthStateChanged(null) will set loading=false cleanly.
        await signOut(auth);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  async function login(email: string, password: string): Promise<void> {
    const auth = getAuth();
    setError(null);
    // signInWithEmailAndPassword triggers onAuthStateChanged above,
    // which fetches the user profile from /api/users/me.
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function logout(): Promise<void> {
    const auth = getAuth();
    setUser(null);
    setError(null);
    await signOut(auth);
  }

  async function refreshUser(): Promise<void> {
    try {
      const response = await api.get<User>('/api/users/me');
      setUser(response.data);
    } catch {
      // Non-fatal: stale profile is preferable to crashing the success path.
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used inside <AuthProvider>');
  }
  return ctx;
}
